import dayjs from 'dayjs';
import { z } from 'zod';
import * as DirectoryType from './directoryType';

export type ProjectCode = string;

export const statusOptions = ['Active', 'On Hold'];
export type Project = {
  id: number; // The unique identifier for the project
  code: ProjectCode; // A code representing the project
  name: DirectoryType.Name; // The name of the project
  description: string; // A description of the project
  contractor?: string | null; // Contractor for project
  start_date: string; // The start date of the project
  member_ids?: number[]; // Only returned to and writable by project access managers
};

export type ProjectPartial = Partial<Project>;
export type ProjectCreate = Omit<Project, 'id' | 'member_ids'> & {
  member_ids?: number[];
};
export type ProjectUpdate = Partial<ProjectCreate>;
export type ProjectMemberOption = {
  id: number;
  display_name: string;
};

export type ProjectSheet = {
  id: number; // The unique identifier for the project sheet
  created_at: string; // The date and time when the project sheet was created
  updated_at: string; // The date and time when the project sheet was last updated
  name: string; // The name of the project sheet
  description: string; // A description of the project sheet
  project: Project['id']; // The identifier for the project associated with the project sheet
  created_by: number;
};

export type ProjectSheetCreate = Pick<
  ProjectSheet,
  'name' | 'description' | 'project'
>;

export const projectSheetTaskSchema = z
  .object({
    id: z.coerce.number().optional(),
    created_by: z.coerce.number(),
    sheet: z.coerce.number(),
    title: z.string().min(1, { error: 'Title is required' }),
    description: z.string().optional(),
    start_date: z.string().min(1, { error: 'Start date is required' }),
    due_date: z.string().min(1, { error: 'Due date is required' }),
    assigned_to: z
      .array(
        z.coerce
          .number({ message: 'Assigned IDs must be numbers' })
          .int({ message: 'Assigned IDs must be integers' })
          .positive({ message: 'Assigned IDs must be positive' }),
      )
      .optional(),
    status: z.string().optional(),
    priority: z.string().optional(),
    estimated_hours: z
      .union([z.coerce.number().min(0), z.undefined()])
      .optional(),
    actual_hours: z.union([z.coerce.number().min(0), z.undefined()]).optional(),
    parent: z.union([z.coerce.number(), z.null(), z.undefined()]).optional(),
  })
  // Validate that due_date > start_date
  .refine(
    (data) => {
      if (!data.start_date || !data.due_date) return true;
      const start = dayjs(data.start_date);
      const due = dayjs(data.due_date);
      return due.isAfter(start);
    },
    {
      error: 'Due date must be after start date',
      path: ['due_date'],
    },
  )
  // Validate start_date < due_date (mirror check)
  .refine(
    (data) => {
      if (!data.start_date || !data.due_date) return true;
      const start = dayjs(data.start_date);
      const due = dayjs(data.due_date);
      return start.isBefore(due);
    },
    {
      error: 'Start date must be before due date',
      path: ['start_date'],
    },
  );

export type ProjectSheetTaskSchema = z.infer<typeof projectSheetTaskSchema>;
